/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, FormControl, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { removeLeadingWhitespace } from '../../shared/utils/stringUtils';
import AssistiveText from '../submission/templates/AssistiveText';

const useStyles = makeStyles(theme => ({
  root: ({ hasErrors, hasWarnings }) => ({
    '& fieldset': () => ({
      borderColor: `${(hasErrors && theme.palette.error.main) || (hasWarnings && theme.palette.warning.main)}`,
      border: `${(hasErrors && `2px solid ${theme.palette.error.main}`) ||
        (hasWarnings && `2px solid ${theme.palette.warning.main}`) ||
        (!hasWarnings && !hasErrors && `calc(1rem / 16) solid ${theme.palette.input}`)}`,
    }),
    '& .MuiFormLabel-root.Mui-error': {
      color: theme.palette.mainText,
    },
  }),
  customWarning: {
    '& .MuiOutlinedInput-marginDense fieldset': {
      border: `2px solid ${theme.palette.warning.main}`,
    },
    '& .MuiInputLabel-formControl': {
      color: theme.palette.mainText,
    },
  },
  customError: {
    '& .MuiOutlinedInput-marginDense fieldset': {
      border: `2px solid ${theme.palette.error.main}`,
    },
    '& .MuiInputLabel-formControl': {
      color: theme.palette.mainText,
    },
  },
  flexDiv: {
    display: 'flex',
    flexDirection: 'column',
    width: '19rem',
  },
  tooltip: {
    fontSize: '1rem',
  },
}));

export function CustomTextField({
  id,
  name,
  defaultValue,
  fullWidth,
  multiline,
  rows,
  InputProps,
  className,
  inputRef,
  label,
  placeholder,
  readOnly,
  disabled,
  onChange,
  onBlur,
  onFocus,
  onKeyUp,
  size,
  errors,
  value,
  required,
  testId,
  hasWarning,
  hasError,
  showTooltip,
}) {
  const { t } = useTranslation();
  const hasErrors = (errors && errors[name] && t(errors[name]?.message)) || hasError;
  const classes = useStyles({ hasErrors, hasWarning });
  const classNames = clsx(hasWarning && classes.customWarning, hasErrors && classes.customError);
  const [tooltipActive, setTooltipActive] = useState(false);
  const displayLabel = showTooltip && label?.length > 30 ? `${label.slice(0, 30)}...` : label;

  const onMouseEnterCallback = () => {
    setTooltipActive(true);
  };

  const onMouseExitCallback = () => {
    setTooltipActive(false);
  };

  return (
    <FormControl className={clsx(className, classes.root)}>
      <Tooltip placement="top" open={showTooltip && tooltipActive} title={<p className={classes.tooltip}>{label}</p>}>
        <div
          onMouseEnter={onMouseEnterCallback}
          onMouseLeave={onMouseExitCallback}
          tabIndex={-1}
          className={classes.flexDiv}
        >
          <TextField
            required={required}
            title=""
            id={id}
            name={name}
            className={classNames}
            defaultValue={defaultValue}
            variant="outlined"
            value={value || undefined}
            fullWidth={fullWidth}
            multiline={multiline}
            minRows={rows}
            InputProps={InputProps}
            error={!!hasErrors}
            inputRef={inputRef}
            label={displayLabel}
            placeholder={placeholder}
            readOnly={readOnly}
            disabled={disabled}
            onChange={(event) => {
              removeLeadingWhitespace(event);
              if (onChange instanceof Function) {
                onChange(event);
              }
            }}
            data-testid={testId}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyUp={onKeyUp}
            size={size}
          />
          {!!hasErrors && (
            <AssistiveText data-testid="helper-text" severity="error">
              {hasErrors}
            </AssistiveText>
          )}
        </div>
      </Tooltip>
    </FormControl>
  );
}
